import { useEffect, useState } from "react";
import { ArrowUpRight, Clock } from "lucide-react";
import { api } from "@/lib/api";

const DRILL_IMG =
  "https://images.unsplash.com/photo-1562237553-36ad661d6f2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxkcmlsbCUyMHJpZyUyMG1pbmluZ3xlbnwwfHx8fDE3Nzg5NzQxMTd8MA&ixlib=rb-4.1.0&q=85";

const REGION_LABELS = {
  "rgn-bc": "British Columbia",
  "rgn-ontario": "Ontario",
  "rgn-quebec": "Quebec",
  "rgn-saskatchewan": "Saskatchewan",
  "rgn-manitoba": "Manitoba",
  "rgn-newfoundland-labrador": "Newfoundland & Labrador",
  "rgn-yukon": "Yukon",
  "rgn-nwt": "Northwest Territories",
  "rgn-nunavut": "Nunavut",
  "rgn-new-brunswick": "New Brunswick",
};

const estimateReadMinutes = (content = "") =>
  Math.max(2, Math.round(content.split(/\s+/).length / 180));

const formatTime = (iso) =>
  new Date(iso).toLocaleString("en-CA", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export const DailyDrilldown = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let mounted = true;
    api
      .get("/articles", { params: { limit: 5 } })
      .then((r) => mounted && Array.isArray(r.data) && setItems(r.data))
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  if (items.length === 0) return null;
  const [lead, ...rest] = items;
  const leadRegion = REGION_LABELS[lead.region] || "National";
  const leadRead = estimateReadMinutes(lead.content);

  return (
    <section
      id="drilldown"
      className="py-14 lg:py-20 border-b border-border"
      data-testid="daily-drilldown-section"
    >
      <div className="bb-container">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="bb-overline text-bb-copper">Daily Drilldown</div>
            <h2 className="font-archivo text-3xl lg:text-4xl text-bb-navy mt-2 font-bold tracking-tight">
              Today on the desk.
            </h2>
          </div>
          <a
            href="/news"
            className="bb-link text-xs font-archivo uppercase tracking-widest text-bb-navy inline-flex items-center gap-1"
            data-testid="drilldown-view-all"
          >
            View all dispatches <ArrowUpRight size={14} strokeWidth={2.4} />
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-border border border-border">
          {/* Lead story */}
          {lead && (
            <article
              className="bg-white lg:col-span-7 flex flex-col group"
              data-testid="drilldown-lead-story"
            >
              <div className="relative overflow-hidden border-b border-border">
                <img
                  src={lead.featuredImage || DRILL_IMG}
                  alt={lead.title}
                  className="bb-img w-full aspect-[16/9] object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                />
                <span className="absolute top-4 left-4 bg-bb-copper text-white font-archivo text-[10px] tracking-[0.2em] uppercase px-3 py-1.5">
                  {lead.category}
                </span>
              </div>
              <div className="p-7 lg:p-9 flex flex-col gap-4">
                <div className="bb-overline text-bb-slate flex items-center gap-3">
                  <span>{leadRegion}</span>
                  <span className="h-px w-6 bg-bb-navy/30" />
                  <span className="font-mono normal-case tracking-normal">
                    {formatTime(lead.publishedDate)}
                  </span>
                </div>
                <h3 className="font-archivo text-2xl lg:text-4xl text-bb-navy font-bold tracking-tight leading-[1.1]">
                  <a href={`/news/${lead.slug}`} className="bb-link">
                    {lead.title}
                  </a>
                </h3>
                <p className="text-base text-bb-slate leading-relaxed">
                  {lead.excerpt}
                </p>
                <div className="flex items-center gap-4 pt-4 border-t border-border mt-2">
                  <span className="text-xs font-mono text-bb-slate">
                    By {lead.author}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs font-mono text-bb-slate ml-auto">
                    <Clock size={12} /> {leadRead} min read
                  </span>
                </div>
              </div>
            </article>
          )}

          {/* Sidebar list */}
          <div
            className="lg:col-span-5 bg-white flex flex-col"
            data-testid="drilldown-sidebar"
          >
            {rest.slice(0, 4).map((n, i) => {
              const r = REGION_LABELS[n.region] || "National";
              const mins = estimateReadMinutes(n.content);
              return (
                <article
                  key={n.id}
                  className={`p-6 lg:p-7 flex flex-col gap-2 hover:bg-bb-offwhite transition-colors group ${
                    i !== rest.slice(0, 4).length - 1
                      ? "border-b border-border"
                      : ""
                  }`}
                  data-testid={`drilldown-item-${n.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="bb-overline text-bb-copper">
                      {n.category}
                    </span>
                    <span className="text-[11px] font-mono text-bb-slate">
                      {formatTime(n.publishedDate)}
                    </span>
                  </div>
                  <h4 className="font-archivo text-base lg:text-lg text-bb-navy font-semibold leading-snug tracking-tight">
                    <a href={`/news/${n.slug}`} className="bb-link">
                      {n.title}
                    </a>
                  </h4>
                  <div className="flex items-center gap-3 text-[11px] font-mono text-bb-slate mt-1">
                    <span>{r}</span>
                    <span className="opacity-40">·</span>
                    <span className="inline-flex items-center gap-1">
                      <Clock size={11} /> {mins} min
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DailyDrilldown;
